import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CATEGORY_OPTIONS } from '../lib/equipmentFilters';

// URL-driven filter state so filters persist when navigating between
// /equipment and /equipment/:id (and survive refresh/bookmarks).
export const useEquipmentFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawCategory = searchParams.get('category');
  const selectedCategory = CATEGORY_OPTIONS.includes(rawCategory) ? rawCategory : 'All';

  const filters = {
    searchQuery: searchParams.get('q') || '',
    selectedCategory,
    selectedEquipmentType: searchParams.get('type') || 'All',
    selectedRange: searchParams.get('range') || 'All',
    selectedPounds: searchParams.get('pounds') || 'All',
    maxPrice: searchParams.get('maxPrice') || '',
    priceTimeframe: searchParams.get('timeframe') || 'daily',
  };

  const update = useCallback(
    (patch) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const setOrDelete = (key, val, defaultVal) => {
          if (val === undefined) return;
          if (val === '' || val === null || val === defaultVal) {
            next.delete(key);
          } else {
            next.set(key, val);
          }
        };
        if ('searchQuery' in patch) setOrDelete('q', patch.searchQuery, '');
        if ('selectedCategory' in patch) setOrDelete('category', patch.selectedCategory, 'All');
        if ('selectedEquipmentType' in patch) setOrDelete('type', patch.selectedEquipmentType, 'All');
        if ('selectedRange' in patch) setOrDelete('range', patch.selectedRange, 'All');
        if ('selectedPounds' in patch) setOrDelete('pounds', patch.selectedPounds, 'All');
        if ('maxPrice' in patch) setOrDelete('maxPrice', patch.maxPrice, '');
        if ('priceTimeframe' in patch) setOrDelete('timeframe', patch.priceTimeframe, 'daily');
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  // Changing category wipes dependent selections
  const setCategory = useCallback(
    (value) => {
      update({
        selectedCategory: value,
        selectedEquipmentType: 'All',
        selectedRange: 'All',
        selectedPounds: 'All',
      });
    },
    [update]
  );

  const setEquipmentType = useCallback(
    (value) => {
      update({
        selectedEquipmentType: value,
        selectedRange: 'All',
        selectedPounds: 'All',
      });
    },
    [update]
  );

  const clear = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  // Serialize current params into a query string for use when preserving filters across links
  const toQueryString = useCallback(() => {
    const qs = searchParams.toString();
    return qs ? `?${qs}` : '';
  }, [searchParams]);

  return {
    filters,
    setSearchQuery: (v) => update({ searchQuery: v }),
    setCategory,
    setEquipmentType,
    setRange: (v) => update({ selectedRange: v }),
    setPounds: (v) => update({ selectedPounds: v }),
    setMaxPrice: (v) => update({ maxPrice: v }),
    setPriceTimeframe: (v) => update({ priceTimeframe: v }),
    clear,
    toQueryString,
  };
};
